import { createSelector } from "@reduxjs/toolkit";
import _ from "lodash";
import { SimplifyAP } from "simplify-ts";
import { Vector3 } from "three";

import { degToRad, metersToFeet } from "@skydio/math";
import { ReservedWaypointKeys } from "@skydio/pbtypes/pbtypes/gen/skills/reserved_waypoint_keys_pb";
import { NavSourceInfo } from "@skydio/pbtypes/pbtypes/vehicle/ambassador/ambassador_pb";
import { convertTransPbToObject3 } from "@skydio/shared_ui/utils/math";

import { getIsInDfrCommand } from "components/main/teleop/util/iframeHelpers";
import { quantizeAngle } from "components/main/teleop/util/navigation-utils";
import {
  distanceFromLatLng,
  getCurrentCameraHeadingAngle,
  getCurrentNavCameraHeadingAngle,
  getTotalFlightPathDistance,
  latLngArrayFromGps,
} from "utils/map";

import type { AppState } from "state/store";
import type { PaneType } from "./slice";
import { StoredWaypointKeys } from "@skydio/pbtypes/pbtypes/vehicle/return_manager/stored_waypoint_pb";

// default decimal precision to use with GPS coordinates (~1.1m)
export const DEFAULT_GPS_PRECISION = 5;

const metersToFeetRounded = (meters: number) => _.round(metersToFeet(meters));

export const isFullscreen = (state: AppState) => state.flightPlayer.fullscreenState.isFullscreen;

export const getTrajectoryTypes = (state: AppState) => state.flightPlayer.trajectoryTypes;

export const isPlaying = (state: AppState) => state.flightPlayer.playing;
export const getFlightDuration = (state: AppState) => state.flightPlayer.duration;
export const getFlightInfo = (state: AppState) => state.flightPlayer.flightInfo;
export const getRecordingStartTime = (state: AppState) => state.flightPlayer.recordingStartTime;
export const getConvergedGPSPoint = (state: AppState) => state.flightPlayer.convergedGPSPoint;
export const getConvergedGlobalYawNav = (state: AppState) =>
  state.flightPlayer.convergedGlobalYawNav;
export const getCameraDollyFollowData = (state: AppState) =>
  state.flightPlayer.cameraDollyFollowData;
export const getCurrentPlaybackTime = (state: AppState) => state.flightPlayer.playbackTime;
export const getCurrentSeekTime = (state: AppState) => state.flightPlayer.seekTime;
export const getCurrentPlaybackTimeWithRecordingOffset = createSelector(
  getCurrentPlaybackTime,
  getRecordingStartTime,
  (playbackTime, recordingStartTime) => playbackTime + recordingStartTime
);

export const getPlaybackRate = (state: AppState) => state.flightPlayer.playbackRate;

export const getVisiblePanes = (state: AppState) => {
  const isInDfrCommand = getIsInDfrCommand();

  if (state.flightPlayer.panes === null) {
    // Handle default pane configuration depending on feature flag
    const isSplitViewDefault = !isInDfrCommand;

    return (isSplitViewDefault ? ["video", "map"] : ["video"]) as PaneType[];
  }
  return state.flightPlayer.panes;
};

export const isVideoShown = (state: AppState) => getVisiblePanes(state).includes("video");
export const isMapShown = (state: AppState) => getVisiblePanes(state).includes("map");
export const getIsMapFullscreen = (state: AppState) =>
  getVisiblePanes(state).includes("map") && getVisiblePanes(state).length === 1;

export const getMapViewport = (state: AppState) => state.flightPlayer.mapViewport;

export const getMapStyle = (state: AppState) => state.flightPlayer.mapStyle;

export const getCurrentVehicleAndCameraPoseLite = (state: AppState) =>
  state.flightPlayer.telemetry.vehicleAndCameraPoseLite.current;

export const getVehicleTCameraOffset = createSelector(
  getCurrentVehicleAndCameraPoseLite,
  poseLite =>
    poseLite?.vehicleTCamera
      ? convertTransPbToObject3(poseLite.vehicleTCamera).position
      : new Vector3()
);

export const getCurrentVehicleStatus = (state: AppState) => {
  return state.flightPlayer.telemetry.vehicleStatus.current;
};
const getCurrentReturnWaypoints = (state: AppState) => {
  return state.flightPlayer.telemetry.returnWaypoints.current;
};
export const getCurrentFlightPlaybackVehicleStatus = (state: AppState) =>
  state.flightPlayer.telemetry.vehicleStatus.currentPlayback;
export const getAllPlaybackVehicleStatuses = (state: AppState) =>
  state.flightPlayer.telemetry.vehicleStatus.allPlayback;
export const getAllGpsData = createSelector(getAllPlaybackVehicleStatuses, vehicleStatuses =>
  vehicleStatuses.filter(status => !!status.vehicleGps).map(status => status.vehicleGps!)
);
export const getAllGpsDataWithTimestamps = createSelector(
  getAllPlaybackVehicleStatuses,
  vehicleStatuses =>
    vehicleStatuses
      .filter(status => !!status.vehicleGps)
      .map(status => ({
        utime: status.utime,
        gps: status.vehicleGps!,
        gpsConverged: status.gpsConverged,
        globalYawNav: status.globalYawNav,
        flightState: status.flightState,
        taskKind: status.activeTaskKind,
        handTakeoffState: status.handTakeoffState,
        groundHeight: status.groundHeight,
        heightAboveTakeoff: status.heightAboveTakeoff,
      }))
);

export const getAllPlaybackUserCameraStatuses = (state: AppState) =>
  state.flightPlayer.telemetry.userCameraStatus.allPlayback;

export const getAllPlaybackUserCameraStatusesNG = (state: AppState) =>
  state.flightPlayer.telemetry.userCameraStatusNG.allPlayback;

export const getCurrentGpsData = createSelector(
  getCurrentVehicleStatus,
  vehicleStatus => vehicleStatus?.vehicleGps
);
export const getCurrentFlightPlaybackGpsData = createSelector(
  getCurrentFlightPlaybackVehicleStatus,
  vehicleStatus => vehicleStatus?.vehicleGps
);

export const getCurrentWind = createSelector(
  getCurrentVehicleStatus,
  vehicleStatus => vehicleStatus?.wind
);

export const getCurrentWindSpeedRounded = createSelector(getCurrentWind, wind =>
  wind?.speed != null ? Math.round(wind?.speed) : undefined
);

export const getCurrentWindHeadingQuantized = createSelector(getCurrentWind, wind =>
  wind?.heading != null ? quantizeAngle(wind?.heading) : undefined
);

export const getTakeoffGpsData = createSelector(getAllGpsData, all =>
  all.length ? all[0] : undefined
);

export const getSimplifiedFlightPath = createSelector(getAllGpsDataWithTimestamps, msgs => {
  // Filter out all [0, 0] data points
  const gpsData = msgs.filter(x => x.gpsConverged);
  return SimplifyAP(
    gpsData.map(x => latLngArrayFromGps(x.gps)),
    1e-6
  );
});

export const getDoesGpsConverge = createSelector(getAllGpsDataWithTimestamps, msgs => {
  return msgs.some(x => x.gpsConverged);
});

export const getCurrentLatWithPrecision = (precision: number = DEFAULT_GPS_PRECISION) =>
  createSelector([getCurrentGpsData], gps => (gps ? _.round(gps.latitude, precision) : 0));

export const getCurrentLngWithPrecision = (precision: number = DEFAULT_GPS_PRECISION) =>
  createSelector([getCurrentGpsData], gps => (gps ? _.round(gps.longitude, precision) : 0));

export const getCurrentFlightPlaybackLatWithPrecision = (
  precision: number = DEFAULT_GPS_PRECISION
) =>
  createSelector([getCurrentFlightPlaybackGpsData], gps =>
    gps ? _.round(gps.latitude, precision) : 0
  );

export const getCurrentGpsAltitude = createSelector(getCurrentGpsData, gps =>
  gps ? gps.altitude : null
);

export const getCurrentFlightPlaybackGpsAltitude = createSelector(
  getCurrentFlightPlaybackGpsData,
  gps => (gps ? gps.altitude : null)
);

export const getCurrentFlightPlaybackLngWithPrecision = (
  precision: number = DEFAULT_GPS_PRECISION
) =>
  createSelector([getCurrentFlightPlaybackGpsData], gps =>
    gps ? _.round(gps.longitude, precision) : 0
  );

export const getCurrentLatLngWithPrecision = (precision: number = DEFAULT_GPS_PRECISION) =>
  createSelector<AppState, number, number, [number, number]>(
    [getCurrentLatWithPrecision(precision), getCurrentLngWithPrecision(precision)],
    (lat, lng) => [lat, lng]
  );
export const getCurrentFlightPlaybackLatLngWithPrecision = (
  precision: number = DEFAULT_GPS_PRECISION
) =>
  createSelector<AppState, number, number, [number, number]>(
    [
      getCurrentFlightPlaybackLatWithPrecision(precision),
      getCurrentFlightPlaybackLngWithPrecision(precision),
    ],
    (lat, lng) => [lat, lng]
  );

export const getTakeoffLatLng = createSelector(getTakeoffGpsData, gps =>
  gps ? latLngArrayFromGps(gps) : undefined
);

const getFlightDistance = createSelector(getSimplifiedFlightPath, getTotalFlightPathDistance);
export const getFlightDistanceFt = createSelector(getFlightDistance, metersToFeetRounded);
export const getCurrentPose = (state: AppState) => state.flightPlayer.telemetry.pose.current;
export const getAllPlaybackPoses = (state: AppState) =>
  state.flightPlayer.telemetry.pose.allPlayback;
export const getSimplifiedPosesList = createSelector(getAllPlaybackPoses, poses =>
  poses.slice(0, poses.length)
);

export const getCurrentVelocity = createSelector(
  getCurrentPose,
  pose => new Vector3(...(pose?.velocity_global ?? []))
);
export const getCurrentSpeed = createSelector(getCurrentVelocity, velocity => velocity.length());
export const getCurrentSpeedMph = createSelector(getCurrentSpeed, speed => _.round(speed * 2.237));
export const getCurrentSpeedMphFromStatus = createSelector(getCurrentGpsData, gps =>
  gps?.groundSpeed ? _.round(gps.groundSpeed * 2.237) : 0
);

export const getCurrentFlightPlaybackPose = (state: AppState) =>
  state.flightPlayer.telemetry.pose.currentPlayback;
export const getCurrentFlightPlaybackVelocity = createSelector(
  getCurrentFlightPlaybackPose,
  pose => new Vector3(...(pose?.velocity_global ?? []))
);
export const getCurrentFlightPlaybackSpeed = createSelector(
  getCurrentFlightPlaybackVelocity,
  velocity => velocity.length()
);
export const getCurrentFlightPlaybackSpeedMph = createSelector(
  getCurrentFlightPlaybackSpeed,
  speed => _.round(speed * 2.237)
);

export const getFlightHasTelemetry = createSelector(
  getSimplifiedFlightPath,
  flightPath => flightPath.length > 0
);

export const getCurrentHeight = createSelector(
  getCurrentVehicleStatus,
  vehicleStatus => vehicleStatus?.heightAboveTakeoff?.height ?? 0
);
export const getCurrentHeightFt = createSelector(getCurrentHeight, metersToFeetRounded);

export const getCurrentFlightPlaybackHeight = createSelector(
  getCurrentFlightPlaybackVehicleStatus,
  vehicleStatus => vehicleStatus?.heightAboveTakeoff?.height ?? 0
);

export const getCurrentAltitudeAgl = createSelector(
  getCurrentVehicleStatus,
  vehicleStatus => vehicleStatus?.altitudeAgl ?? null
);

export const getCurrentAltitudeAglPlayback = createSelector(
  getCurrentFlightPlaybackVehicleStatus,
  vehicleStatus => vehicleStatus?.altitudeAgl ?? null
);

export const getCurrentNumSatellites = createSelector(
  getCurrentVehicleStatus,
  vehicleStatus => vehicleStatus?.vehicleGps?.numSatellitesUsed
);

const getCurrentGlobalYawNav = createSelector(
  getCurrentVehicleStatus,
  vehicleStatus => vehicleStatus?.globalYawNav
);

// Returns angle in radians
export const getCurrentVelocityAngle = createSelector(
  getCurrentVelocity,
  getCurrentGlobalYawNav,
  getMapViewport,
  (velocity, yawOffset, viewport) => {
    /**
     * NOTE(trey): ensuring the velocity vector's length exceeds a threshold reduces direction
     * changes caused by noise while stationary.
     */
    if (velocity.length() > 0.001 && !_.isUndefined(yawOffset)) {
      // use vehicle pose if available
      const navAngle = Math.atan2(velocity.y, velocity.x);
      const worldAngle = Math.PI / 2 - (navAngle + yawOffset);
      // Correct for map viewport
      return worldAngle - degToRad(viewport?.bearing ?? 0);
    }
  }
);

export const getDistanceFromLaunch = createSelector(
  getTakeoffGpsData,
  getCurrentGpsData,
  (current, takeoff) => {
    if (!current || !takeoff) return 0;
    return distanceFromLatLng(latLngArrayFromGps(takeoff), latLngArrayFromGps(current));
  }
);

export const getDistanceFromLaunchFt = createSelector(getDistanceFromLaunch, metersToFeetRounded);
export const getDistanceFromLaunchFtFromStatus = createSelector(
  getCurrentVehicleStatus,
  vehicleStatus => {
    const takeoffLatLng: [number, number] = [
      vehicleStatus?.lostConnectionDestination?.latitude ?? 0,
      vehicleStatus?.lostConnectionDestination?.longitude ?? 0,
    ];
    const currentLatLng: [number, number] = [
      vehicleStatus?.vehicleGps?.latitude ?? 0,
      vehicleStatus?.vehicleGps?.longitude ?? 0,
    ];
    if (!takeoffLatLng[0] || !takeoffLatLng[1] || !currentLatLng[0] || !currentLatLng[1]) return 0;
    return metersToFeetRounded(distanceFromLatLng(takeoffLatLng, currentLatLng));
  }
);

export const getFlightPlayerTelemetry = (state: AppState) => state.flightPlayer.telemetry;

export const getCurrentBatteryPrediction = createSelector(
  getFlightPlayerTelemetry,
  telemetry => telemetry.battery.current
);
export const getCurrentFlightPlaybackBatteryPrediction = createSelector(
  getFlightPlayerTelemetry,
  telemetry => telemetry.battery.currentPlayback
);

/**
 * @deprecated gps_data will no longer be available in future versions of the vehicle.
 * */
const getCurrentGpsDataTelemetry = (state: AppState) => {
  return state.flightPlayer.telemetry.gps_data.current;
};

// Old vehicles publish GPS_WAYPOINT_STATUS_PB (gps_data); new vehicles publish
// RETURN_WAYPOINTS_PB (returnWaypoints) and add activeReturnInfo to vehicleStatus.
// Since vehicleStatus exists on both old and new vehicles, we use the presence of
// gps_data to distinguish: if gps_data is present, use old fields; otherwise use new.
export const getCurrentDistanceFromTakeoff = createSelector(
  getCurrentGpsDataTelemetry,
  getCurrentReturnWaypoints,
  (gpsTelemetry, returnWaypoints) => {
    if (gpsTelemetry) {
      return metersToFeetRounded(gpsTelemetry.distanceToTakeoff);
    }
    const distInMeters =
      returnWaypoints?.waypointTakeoff?.returnWaypoint?.straightLineDistance2d ?? 0;
    return metersToFeetRounded(distInMeters);
  }
);

export const getCurrentBatteryLevel = createSelector(getCurrentBatteryPrediction, prediction => {
  if (!prediction) {
    return 0;
  }
  return prediction.trueBatteryPercentage;
});

export const getCurrentFlightPlaybackBatteryLevel = createSelector(
  getCurrentFlightPlaybackBatteryPrediction,
  prediction => {
    if (!prediction) {
      return 0;
    }
    return prediction.trueBatteryPercentage;
  }
);
export const isBatteryCharging = createSelector(getCurrentBatteryPrediction, prediction => {
  if (!prediction) {
    return false;
  }
  return prediction.batteryCharging;
});
export const getCurrentBatteryPredictedReturnPercentage = createSelector(
  getCurrentBatteryPrediction,
  prediction => {
    if (!prediction) {
      return 0;
    }
    return prediction.predictedReturnPercentage;
  }
);
export const getCurrentBatteryPredictedCriticalPercentage = createSelector(
  getCurrentBatteryPrediction,
  prediction => {
    if (!prediction) {
      return 0;
    }
    return prediction.predictedCriticalPercentage;
  }
);
export const getCurrentBatteryLowLandSecondsRemaining = createSelector(
  getCurrentBatteryPrediction,
  prediction => {
    if (!prediction) {
      return -1;
    }
    // if battery is already critical, return -1 to indicate that the battery is already too low or there is an invalid prediction
    if (
      prediction.predictedLowLandUtime <= 0 ||
      prediction.predictedBatteryHotUtime <= 0 ||
      prediction.trueBatteryPercentage <= 0
    ) {
      return -1;
    }
    // get the minimum time between the too hot predicted time and the too low predicted time
    const temperatureUsRemaining = prediction.predictedBatteryHotUtime - prediction.utime;
    const capacityUsRemaining = prediction.predictedLowLandUtime - prediction.utime;
    if (temperatureUsRemaining <= 0 || capacityUsRemaining <= 0) {
      return 0;
    }
    return Math.min(temperatureUsRemaining, capacityUsRemaining) / 1e6; // convert to seconds
  }
);
export const isFlightPlaybackBatteryCharging = createSelector(
  getCurrentFlightPlaybackBatteryPrediction,
  prediction => {
    if (!prediction) {
      return false;
    }
    return prediction.batteryCharging;
  }
);
export const getCurrentFlightPlaybackBatteryPredictedReturnPercentage = createSelector(
  getCurrentFlightPlaybackBatteryPrediction,
  prediction => {
    if (!prediction) {
      return 0;
    }
    return prediction.predictedReturnPercentage;
  }
);
export const getCurrentFlightPlaybackBatteryPredictedCriticalPercentage = createSelector(
  getCurrentFlightPlaybackBatteryPrediction,
  prediction => {
    if (!prediction) {
      return 0;
    }
    return prediction.predictedCriticalPercentage;
  }
);
export const getCurrentFlightPlaybackBatteryLowLandSecondsRemaining = createSelector(
  getCurrentFlightPlaybackBatteryPrediction,
  prediction => {
    if (!prediction) {
      return -1;
    }
    // if battery is already critical, return -1 to indicate that the battery is already too low or there is an invalid prediction
    if (
      prediction.predictedLowLandUtime <= 0 ||
      prediction.predictedBatteryHotUtime <= 0 ||
      prediction.trueBatteryPercentage <= 0
    ) {
      return -1;
    }
    // get the minimum time between the too hot predicted time and the too low predicted time
    const temperatureUsRemaining = prediction.predictedBatteryHotUtime - prediction.utime;
    const capacityUsRemaining = prediction.predictedLowLandUtime - prediction.utime;
    if (temperatureUsRemaining <= 0 || capacityUsRemaining <= 0) {
      return 0;
    }
    return Math.min(temperatureUsRemaining, capacityUsRemaining) / 1e6; // convert to seconds
  }
);

export const getCurrentGimbalNavTransform = (state: AppState) =>
  state.flightPlayer.telemetry.gimbalNavTransform.current;

export const getCameraHeadingAngleFloor = (interval: number) =>
  createSelector(
    getCurrentGlobalYawNav,
    getCurrentGimbalNavTransform,
    (globalYawNav, gimbalNavTransform) => {
      if (
        !_.isNumber(globalYawNav) ||
        !gimbalNavTransform?.navTGimbalCameraImu?.orientation?.xyzwList
      ) {
        return 0;
      }
      const headingAngle = getCurrentCameraHeadingAngle(
        gimbalNavTransform.navTGimbalCameraImu.orientation.xyzwList,
        globalYawNav
      );
      return headingAngle - (headingAngle % interval);
    }
  );

export const getCameraNavHeading = createSelector(
  getCurrentGimbalNavTransform,
  gimbalNavTransform => {
    if (!gimbalNavTransform?.navTGimbalCameraImu?.orientation?.xyzwList) {
      return undefined;
    }
    const navHeading = getCurrentNavCameraHeadingAngle(
      gimbalNavTransform.navTGimbalCameraImu.orientation.xyzwList
    );
    return navHeading;
  }
);

export const getCameraNavHeadingQuantized = createSelector(getCameraNavHeading, navHeading =>
  navHeading != null ? quantizeAngle(navHeading) : undefined
);

// allow only map view if splitscreen is not enabled
export const getPanesSelector = (allowSplitscreen: boolean) =>
  createSelector([getVisiblePanes], panes =>
    panes.filter(paneType => allowSplitscreen || paneType === "map")
  );

export const getGPSTracks = (state: AppState) => state.flightPlayer.gpsTracks;
export const getGPSShadowTracks = (state: AppState) => state.flightPlayer.gpsShadowTracks;
export const getNAVTracks = (state: AppState) => state.flightPlayer.navTracks;
export const getNAVShadowTracks = (state: AppState) => state.flightPlayer.navShadowTracks;
export const getIsDataForPlaybackReady = (state: AppState) =>
  state.flightPlayer.isDataForPlaybackReady;
export const getAirTraffic = (state: AppState) => state.flightPlayer.airTrafficData.localTraffic;
export const getDedroneTraffic = (state: AppState) =>
  state.flightPlayer.airTrafficData.dedroneTraffic;
export const getAirTrafficUpdatedAt = (state: AppState) =>
  state.flightPlayer.airTrafficData.updatedAt;
export const getAdsbStationState = (state: AppState) => state.flightPlayer.adsbStationState;
export const getAirTrafficDisplaySettings = (state: AppState) =>
  state.flightPlayer.airTrafficData.displaySettings;
export const getMapCaptureTrajectory = (state: AppState) => state.flightPlayer.mapCaptureTrajectory;
export const getMapCaptureCompletedTrajectory = (state: AppState) =>
  state.flightPlayer.mapCaptureCompletedTrajectory;

export const getCurrentHomePointWaypoint = createSelector(
  getCurrentGpsDataTelemetry,
  getCurrentReturnWaypoints,
  (gpsTelemetry, returnWaypoints) => ({
    gps: gpsTelemetry?.rallyPointEnabled ? gpsTelemetry.rallyPoint : undefined,
    nav: returnWaypoints?.waypointHome?.returnWaypoint?.waypoint,
  })
);

export const isHomePointSetSelector = createSelector(
  getCurrentGpsDataTelemetry,
  getCurrentReturnWaypoints,
  (gpsTelemetry, returnWaypoints) => {
    if (gpsTelemetry) {
      return gpsTelemetry.rallyPointEnabled;
    }
    return !!returnWaypoints?.waypointHome;
  }
);

export const isFlyingToHomePointSelector = createSelector(
  getCurrentGpsDataTelemetry,
  getCurrentVehicleStatus,
  (gpsTelemetry, vehicleStatus) => {
    if (gpsTelemetry) {
      return (
        gpsTelemetry.isFlyingToWaypoints &&
        (gpsTelemetry.waypointsList ?? []).length > 0 &&
        gpsTelemetry.waypointsList[0]?.key === ReservedWaypointKeys.Enum.HOME_POINT
      );
    }
    return vehicleStatus?.activeReturnInfo?.waypointKey === StoredWaypointKeys.Enum.HOME;
  }
);

export const getDistanceToHomePoint = createSelector(
  getCurrentGpsDataTelemetry,
  getCurrentReturnWaypoints,
  (gpsTelemetry, returnWaypoints) => {
    if (gpsTelemetry) {
      return gpsTelemetry.distanceToRallyPoint;
    }
    return returnWaypoints?.waypointHome?.returnWaypoint?.straightLineDistance2d ?? 0;
  }
);

export const getDistanceToTakeoffPoint = createSelector(
  getCurrentGpsDataTelemetry,
  getCurrentReturnWaypoints,
  (gpsTelemetry, returnWaypoints) => {
    if (gpsTelemetry) {
      return gpsTelemetry.distanceToTakeoff;
    }
    return returnWaypoints?.waypointTakeoff?.returnWaypoint?.straightLineDistance2d ?? 0;
  }
);

export const getCurrentRtkDataTelemetry = (state: AppState) => {
  return state.flightPlayer.telemetry.rtkGpsData.current;
};

export const getCurrentTakeoffPointWaypoint = createSelector(
  getCurrentGpsDataTelemetry,
  getCurrentReturnWaypoints,
  (gpsTelemetry, returnWaypoints) => ({
    gps: gpsTelemetry?.takeoffSaved ? gpsTelemetry.takeoffPoint : undefined,
    nav: returnWaypoints?.waypointTakeoff?.returnWaypoint?.waypoint,
  })
);

const GPS_ONLY = false; // This could be set to true when implementing a function to retrieve the takeoff point from the map, put as a constant for now
export const isTakeoffPointSetSelector = createSelector(
  getCurrentGpsDataTelemetry,
  getCurrentReturnWaypoints,
  (gpsTelemetry, returnWaypoints) => {
    if (gpsTelemetry) {
      return gpsTelemetry.takeoffSaved || (!GPS_ONLY && gpsTelemetry?.takeoffSavedInNav);
    }
    return !!returnWaypoints?.waypointTakeoff;
  }
);

export const isFlyingToTakeoffPointSelector = createSelector(
  getCurrentGpsDataTelemetry,
  getCurrentVehicleStatus,
  (gpsTelemetry, vehicleStatus) => {
    if (gpsTelemetry) {
      return (
        gpsTelemetry.isFlyingToWaypoints &&
        (gpsTelemetry.waypointsList ?? []).length > 0 &&
        (gpsTelemetry.waypointsList[0]?.key === ReservedWaypointKeys.Enum.LAUNCH ||
          gpsTelemetry.waypointsList[0]?.key === ReservedWaypointKeys.Enum.LAUNCH_NAV)
      );
    }
    return vehicleStatus?.activeReturnInfo?.waypointKey === StoredWaypointKeys.Enum.TAKEOFF;
  }
);

export const hasReceivedTelemetryData = (state: AppState) => {
  return (state.flightPlayer.telemetry.vehicleStatus.current?.utime ?? -1) > 0;
};

export const getVehicleGPSHeading = createSelector(
  getCurrentVehicleStatus,
  status => status?.vehicleGpsHeading
);

export const getHasVehicleGPSConverged = createSelector(
  getCurrentVehicleStatus,
  status => status?.gpsConverged
);

export const getCurrentNavSourceInfo = createSelector(
  getCurrentVehicleStatus,
  status => status?.navSourceInfo
);

export const getIsNightsenseToggleAllowed = createSelector(
  getCurrentVehicleStatus,
  status => status?.nightsenseToggleAllowed
);

export const getVehicleGpsStatus = createSelector(
  getCurrentVehicleStatus,
  status => status?.vehicleGps
);

export const isInAttitudeMode = createSelector(
  getCurrentNavSourceInfo,
  navSource => navSource?.activeNavSource === NavSourceInfo.NavSource.ATTITUDE
);

export const getMapSearchPinPosition = (state: AppState) => state.flightPlayer.mapSearchPinPosition;

export const getRecordAdsbData = (state: AppState) => state.flightPlayer.recordAdsbData;
